/**
 * Audit Log Detail Component
 *
 * Drawer showing full details of an audit log entry.
 * @see US-19a.28
 */

import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  LogIn,
  LogOut,
  User,
  Globe,
  Monitor,
  Calendar,
  Hash,
  Tag,
} from 'lucide-react';
import { Drawer, Badge, Skeleton } from '@/components/ui';
import type { AuditAction } from '@bstock/shared';
import { useAuditLogDetail } from '../hooks/useAuditLogs';
import {
  formatAuditMessage,
  formatAbsoluteDateTime,
  getEntityTypeLabel,
  getActionLabel,
  ACTOR_TYPE_LABELS,
} from '../utils/format-audit-message';
import { cn } from '@/lib/utils';

interface AuditLogDetailProps {
  logId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Get icon for each action type
 */
function getActionIcon(action: AuditAction): React.ReactNode {
  const iconClass = 'w-5 h-5';
  switch (action) {
    case 'CREATE':
      return <Plus className={iconClass} />;
    case 'UPDATE':
      return <Pencil className={iconClass} />;
    case 'DELETE':
      return <Trash2 className={iconClass} />;
    case 'VALIDATE':
      return <CheckCircle className={iconClass} />;
    case 'CANCEL':
      return <XCircle className={iconClass} />;
    case 'LOGIN':
      return <LogIn className={iconClass} />;
    case 'LOGOUT':
      return <LogOut className={iconClass} />;
    default:
      return <Pencil className={iconClass} />;
  }
}

/**
 * Get badge variant for action
 */
function getActionBadgeVariant(action: AuditAction): 'success' | 'primary' | 'danger' | 'warning' | 'neutral' {
  switch (action) {
    case 'CREATE':
    case 'VALIDATE':
      return 'success';
    case 'UPDATE':
      return 'primary';
    case 'DELETE':
      return 'danger';
    case 'CANCEL':
      return 'warning';
    default:
      return 'neutral';
  }
}

/**
 * Info row component
 */
function InfoRow({
  icon,
  label,
  value,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start gap-3 py-3 border-b border-neutral-100 last:border-0', className)}>
      <span className="text-neutral-400 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-neutral-500 mb-0.5">{label}</p>
        <div className="text-sm text-dark-500">{value}</div>
      </div>
    </div>
  );
}

/**
 * JSON display for changes
 */
function JsonDisplay({ data, title }: { data: Record<string, unknown> | null; title: string }) {
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-dark-500 mb-2">{title}</h4>
      <pre className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-xs overflow-auto max-h-60">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

/**
 * Changes display for UPDATE actions
 */
function ChangesDisplay({ changes }: { changes: Record<string, { from: unknown; to: unknown }> | null }) {
  if (!changes || Object.keys(changes).length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-dark-500 mb-2">Modifications</h4>
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg divide-y divide-neutral-200">
        {Object.entries(changes).map(([field, { from, to }]) => (
          <div key={field} className="p-3">
            <p className="text-xs font-medium text-neutral-600 mb-1">{field}</p>
            <div className="flex items-center gap-2 text-xs">
              <span className="bg-danger-50 text-danger-700 px-2 py-0.5 rounded">
                {from === null ? 'null' : String(from)}
              </span>
              <span className="text-neutral-400">→</span>
              <span className="bg-success-50 text-success-700 px-2 py-0.5 rounded">
                {to === null ? 'null' : String(to)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AuditLogDetailDrawer({ logId, open, onOpenChange }: AuditLogDetailProps) {
  const { data: log, isLoading } = useAuditLogDetail(open ? logId : null);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Detail du log"
      description="Informations completes sur cet evenement"
      size="large"
    >
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-40" />
        </div>
      ) : log ? (
        <div>
          {/* Summary card */}
          <div className="bg-neutral-50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className={cn(
                'p-2 rounded-lg',
                log.action === 'CREATE' || log.action === 'VALIDATE' ? 'bg-success-100 text-success-600' :
                log.action === 'UPDATE' ? 'bg-blue-100 text-blue-600' :
                log.action === 'DELETE' ? 'bg-danger-100 text-danger-600' :
                log.action === 'CANCEL' ? 'bg-warning-100 text-warning-600' :
                'bg-neutral-100 text-neutral-600'
              )}>
                {getActionIcon(log.action)}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-dark-500">
                  {formatAuditMessage(log as any)}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  {formatAbsoluteDateTime(log.occurredAt)}
                </p>
              </div>
              <Badge variant={getActionBadgeVariant(log.action)}>
                {getActionLabel(log.action)}
              </Badge>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-0">
            <InfoRow
              icon={<Hash className="w-4 h-4" />}
              label="ID de l'evenement"
              value={<span className="font-mono text-xs">{log.id}</span>}
            />

            <InfoRow
              icon={<Tag className="w-4 h-4" />}
              label="Type d'entite"
              value={getEntityTypeLabel(log.entityType)}
            />

            <InfoRow
              icon={<Hash className="w-4 h-4" />}
              label="ID de l'entite"
              value={<span className="font-mono text-xs">{log.entityId}</span>}
            />

            {log.entityName && (
              <InfoRow
                icon={<Tag className="w-4 h-4" />}
                label="Nom de l'entite"
                value={log.entityName}
              />
            )}

            <InfoRow
              icon={<User className="w-4 h-4" />}
              label="Acteur"
              value={
                <div className="flex items-center gap-2">
                  <span>{log.actorEmail || ACTOR_TYPE_LABELS[log.actorType]}</span>
                  {log.actorType !== 'USER' && (
                    <Badge variant="neutral" className="text-xs">
                      {ACTOR_TYPE_LABELS[log.actorType]}
                    </Badge>
                  )}
                </div>
              }
            />

            {log.actorIp && (
              <InfoRow
                icon={<Globe className="w-4 h-4" />}
                label="Adresse IP"
                value={<span className="font-mono text-xs">{log.actorIp}</span>}
              />
            )}

            {log.actorAgent && (
              <InfoRow
                icon={<Monitor className="w-4 h-4" />}
                label="User Agent"
                value={<span className="text-xs break-all">{log.actorAgent}</span>}
              />
            )}

            <InfoRow
              icon={<Calendar className="w-4 h-4" />}
              label="Date de l'evenement"
              value={formatAbsoluteDateTime(log.occurredAt)}
            />
          </div>

          {/* Changes (for UPDATE actions) */}
          <ChangesDisplay changes={log.changes} />

          {/* Data before (for UPDATE/DELETE) */}
          <JsonDisplay data={log.dataBefore} title="Donnees avant modification" />

          {/* Data after (for CREATE/UPDATE) */}
          <JsonDisplay data={log.dataAfter} title="Donnees apres modification" />

          {/* Metadata */}
          <JsonDisplay data={log.metadata} title="Metadonnees" />
        </div>
      ) : (
        <div className="text-center py-8 text-neutral-500">
          Log non trouve
        </div>
      )}
    </Drawer>
  );
}
